/**
 * Cookie Consent Management Utility
 * Handles user consent for cookies and tracking
 */

// Consent categories
export const CONSENT_CATEGORIES = {
  NECESSARY: 'necessary',
  ANALYTICS: 'analytics', 
  ADVERTISING: 'advertising',
  FUNCTIONAL: 'functional'
};

// Local storage keys
const CONSENT_KEY = 'cookieConsent';
const CONSENT_TIMESTAMP_KEY = 'consentTimestamp';

/**
 * Get current consent status for all categories
 * @returns {Object} Consent status object
 */
export function getConsentStatus() {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      return null; // No consent given yet
    }
    
    const consent = JSON.parse(stored);
    return {
      necessary: true, // Always true - required for site function
      analytics: consent.analytics || false,
      advertising: consent.advertising || false,
      functional: consent.functional || false,
      timestamp: localStorage.getItem(CONSENT_TIMESTAMP_KEY)
    };
  } catch (error) {
    console.error('Error reading consent status:', error);
    return null;
  }
}

/**
 * Save consent preferences
 * @param {Object} consent - Consent preferences object
 */
export function saveConsentStatus(consent) {
  try {
    const consentData = {
      analytics: Boolean(consent.analytics),
      advertising: Boolean(consent.advertising), 
      functional: Boolean(consent.functional)
    };
    
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
    localStorage.setItem(CONSENT_TIMESTAMP_KEY, new Date().toISOString());
    
    console.log('Consent saved:', consentData);
    
    // Trigger consent change event
    window.dispatchEvent(new CustomEvent('consentChanged', { 
      detail: { ...consentData, necessary: true } 
    }));
    
  } catch (error) {
    console.error('Error saving consent:', error);
  }
}

/**
 * Check if user has given consent for analytics
 * @returns {boolean}
 */
export function hasAnalyticsConsent() {
  const consent = getConsentStatus();
  return consent ? consent.analytics : false;
}

/**
 * Check if user has given consent for advertising
 * @returns {boolean}
 */
export function hasAdvertisingConsent() {
  const consent = getConsentStatus();
  return consent ? consent.advertising : false;
}

/**
 * Check if user has given consent for functional cookies
 * @returns {boolean}
 */
export function hasFunctionalConsent() {
  const consent = getConsentStatus();
  return consent ? consent.functional : false;
}

/**
 * Check if user has made any consent choice
 * @returns {boolean}
 */
export function hasConsentChoice() {
  return getConsentStatus() !== null;
}

/**
 * Accept all cookies
 */
export function acceptAllCookies() {
  saveConsentStatus({
    analytics: true,
    advertising: true,
    functional: true
  });
}

/**
 * Reject all non-necessary cookies
 */
export function rejectAllCookies() {
  saveConsentStatus({
    analytics: false,
    advertising: false,
    functional: false
  });
}

/**
 * Clear all consent data (for testing)
 */
export function clearConsent() {
  try {
    localStorage.removeItem(CONSENT_KEY);
    localStorage.removeItem(CONSENT_TIMESTAMP_KEY);
    console.log('Consent data cleared');
  } catch (error) {
    console.error('Error clearing consent:', error);
  }
}

/**
 * Listen for consent changes
 * @param {Function} callback - Function to call when consent changes
 * @returns {Function} Cleanup function
 */
export function onConsentChange(callback) {
  const handler = (event) => {
    callback(event.detail);
  };
  
  window.addEventListener('consentChanged', handler);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('consentChanged', handler);
  };
}

/**
 * Get consent status for display
 * @returns {Object} Human-readable consent status
 */
export function getConsentSummary() {
  const consent = getConsentStatus();
  
  if (!consent) {
    return {
      status: 'No consent given',
      hasChoice: false,
      categories: {}
    };
  }
  
  return {
    status: 'Consent recorded',
    hasChoice: true,
    timestamp: consent.timestamp,
    categories: {
      'Necessary Cookies': 'Always Active',
      'Analytics Cookies': consent.analytics ? 'Accepted' : 'Rejected',
      'Advertising Cookies': consent.advertising ? 'Accepted' : 'Rejected', 
      'Functional Cookies': consent.functional ? 'Accepted' : 'Rejected'
    }
  };
}

// Initialize consent monitoring (placeholder for future Clarity integration)
export function initConsentMonitoring() {
  console.log('Consent monitoring initialized');
  
  // Listen for consent changes and log them
  const cleanup = onConsentChange((consent) => {
    console.log('Consent changed:', consent);
    // Future: This is where Clarity sync would happen
  });
  
  return cleanup;
}